import { CALLBACK_PATH, CLIENT_METADATA_PATH } from './client-metadata';
import { OAUTH_SCOPE } from './scopes';

/**
 * Public URL of the deployment, used to construct the production `client_id`
 * (which points at the hosted client metadata document) and redirect URI.
 *
 * Defaults to the author's domain; forks should override this with the
 * `VITE_OAUTH_CLIENT_URI` environment variable so it matches where they host.
 */
export const PROD_CLIENT_URI: string =
	(import.meta.env.VITE_OAUTH_CLIENT_URI as string | undefined)?.replace(/\/+$/, '') ||
	'https://skeetdeck.j4ck.xyz';

export interface OAuthClientConfig {
	clientId: string;
	redirectUri: string;
	/** Whether we're running against the special atproto loopback client. */
	dev: boolean;
}

const isLoopbackHost = (hostname: string): boolean => {
	return hostname === '127.0.0.1' || hostname === 'localhost' || hostname === '[::1]' || hostname === '::1';
};

/**
 * Resolves the OAuth `client_id` / `redirect_uri` for the current environment.
 *
 * - Development: uses the atproto "loopback" client. Per the spec the
 *   `client_id` host must be `localhost`, while the redirect URI must use the
 *   `127.0.0.1` IP literal (atproto rejects the `localhost` hostname in
 *   redirect URIs). Run the dev server and open it via `http://127.0.0.1:<port>`.
 *
 * - Production: `client_id` points at the hosted client metadata document and
 *   the redirect URI lives on the same origin.
 */
export const getOAuthClientConfig = (): OAuthClientConfig => {
	const { hostname, port, protocol } = location;

	if (isLoopbackHost(hostname)) {
		// Always pin the redirect to the 127.0.0.1 IP literal, regardless of how
		// the page was opened, because atproto requires an IP here.
		const portSuffix = port ? `:${port}` : '';
		const redirectUri = `http://127.0.0.1${portSuffix}${CALLBACK_PATH}`;

		const params = new URLSearchParams();
		params.set('redirect_uri', redirectUri);
		params.set('scope', OAUTH_SCOPE);

		return {
			clientId: `http://localhost?${params.toString()}`,
			redirectUri: redirectUri,
			dev: true,
		};
	}

	// In production we prefer the configured public URL so that the runtime
	// `client_id` matches the statically hosted metadata document exactly.
	// Fall back to the current origin if it happens to differ.
	const base = protocol === 'https:' ? PROD_CLIENT_URI : location.origin;

	return {
		clientId: base + CLIENT_METADATA_PATH,
		redirectUri: base + CALLBACK_PATH,
		dev: false,
	};
};
